import { useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { EDInputGroupContext } from '../BaseComponents/InputGroup/EDInputGroup';
import { useFullscreenView } from './useFullscreenView';

interface IInputGroupInterface {
  inGroup: boolean;
  active: boolean;
  ref: HTMLDivElement | null;
  optionsRef: HTMLDivElement | null;
  onFocus: (event: React.FocusEvent<HTMLElement>) => void;
  navigateNext: () => void;
}

export const useInputGroup = (
  focus?: (event: React.FocusEvent<HTMLElement>) => void,
  disabled?: boolean,
): IInputGroupInterface => {
  const {
    activeId,
    inputGroupFocus,
    ref,
    optionsRef,
    registerContent,
    unregisterContent,
    navigateNext,
  } = useContext(EDInputGroupContext);
  const useFullscreen = useFullscreenView();
  const idRef = useRef<number | null>(null);
  const active = useMemo(
    () => useFullscreen && idRef.current !== null && activeId === idRef.current,
    [activeId, useFullscreen]
  );
  useEffect(() => {
    if (registerContent && !disabled) {
      idRef.current = registerContent();
    }
    return () => {
      if (idRef.current !== null) {
        unregisterContent(idRef.current);
        idRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFocus = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      if (inputGroupFocus && idRef.current !== null) {
        inputGroupFocus(idRef.current);
      }
      if (focus) {
        focus(event);
      }
    },
    [inputGroupFocus, focus]
  );

  return {
    inGroup: !!inputGroupFocus,
    active,
    ref: ref,
    optionsRef: optionsRef,
    onFocus,
    navigateNext,
  };
};
